import { AfterViewInit, Component, EventEmitter, HostListener, Input, NgZone, Output, ViewChild } from '@angular/core';
import {
  ActiveElement,
  ChartDataset,
  ChartEvent,
  ChartOptions,
  ChartTypeRegistry,
  LegendItem,
  ScriptableContext,
} from 'chart.js/dist/types';
import { BaseChartDirective, NgChartsModule } from 'ng2-charts';

import { DeviceMaxSize } from 'app/constants/common.constants';
import { getNewChartFiltersByLabel, getNewFiltersAfterClick, getNumberMonthByName } from 'app/utils/chart.utils';
import { Base64Image, exportChartToBase64Image } from 'app/utils/pdf.utils';
import { IChartSelections } from 'app/models/chart.models';
import { ICitationsByVehicleAgeValue } from 'app/models/vehicle.models';

@Component({
  selector: 'app-vehicle-by-month',
  templateUrl: './vehicle-by-month.component.html',
  styleUrls: ['./vehicle-by-month.component.scss'],
  imports: [NgChartsModule],
})
export class VehicleByMonthComponent implements AfterViewInit {
  constructor(private readonly zone: NgZone) {}

  private vehiclesAgeDataInternal: ICitationsByVehicleAgeValue[] = [];

  @Input() set vehicles(value: ICitationsByVehicleAgeValue[]) {
    this.vehiclesAgeDataInternal = value;
    this.updateChartData();
  }

  get vehicles(): ICitationsByVehicleAgeValue[] {
    return this.vehiclesAgeDataInternal;
  }

  @Input() name = '';
  @Input() newerOlderSelectionName = '';

  private selectionsInternal: IChartSelections = {};

  @Input() set selections(value: IChartSelections) {
    this.selectionsInternal = value;
    this.updateChartData();
  }

  get selections(): IChartSelections {
    return this.selectionsInternal;
  }

  @Input() set isFullScreen(value: boolean) {
    this.fullScreenMode = value;
    this.updateLegend();
  }

  @Output() setSelections: EventEmitter<{
    newFilters: string[];
    resetOther: boolean;
  }> = new EventEmitter();

  @Output() setNewerOlderSelections: EventEmitter<{
    newFilters: string[];
    resetOther: boolean;
  }> = new EventEmitter();
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  private filteredNewData: number[] = [];
  private filteredOldData: number[] = [];
  private totalNewData: number[] = [];
  private totalOldData: number[] = [];

  private fullScreenMode = false;

  public legendOnClick = (e: ChartEvent, legendItem: LegendItem) => {
    if (!e || !e.native || !legendItem || legendItem.datasetIndex == undefined) return;
    const newFilters = getNewFiltersAfterClick(
      this.selections[this.newerOlderSelectionName],
      e.native,
      legendItem.datasetIndex === 0 ? 'newer' : 'older',
    );
    if (newFilters.anyChanges && this.setSelections) {
      this.zone.runTask(() =>
        this.setNewerOlderSelections.emit({
          newFilters: newFilters.newSelectionFilter,
          resetOther: newFilters.resetOtherSelectionFilters,
        }),
      );
    }
  };
  public chartType: keyof ChartTypeRegistry = 'bar';
  public chartLabels: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  public chartData: ChartDataset[] = [
    {
      label: 'Newer          ', // 10 spaces are added because title is ignore by chart js when legend width is calculated
      data: [],
      backgroundColor: (context: ScriptableContext<'bar'>) => {
        const color = '#118DFF';
        return this.getBarBackgroundColor(context, color, this.totalNewData, this.filteredNewData);
      },
      borderWidth: 0,
    },
    {
      label: 'Older',
      data: [],
      backgroundColor: (context: ScriptableContext<'bar'>) => {
        const color = '#12239E';
        return this.getBarBackgroundColor(context, color, this.totalOldData, this.filteredOldData);
      },
      borderWidth: 0,
    },
  ];

  getTotalCount(dataIndex: number) {
    const newTotalCount = this.filteredNewData[dataIndex];
    const oldTotalCount = this.filteredOldData[dataIndex];
    return newTotalCount + oldTotalCount;
  }

  public chartOptions: ChartOptions = {
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: true,
        labels: {
          usePointStyle: true,
          font: {
            size: 12,
          },
        },
        position: 'right',
        align: 'start',
        title: {
          text: 'Release Date',
          display: true,
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        onClick: this.legendOnClick,
      },
      tooltip: {
        callbacks: {
          beforeTitle: (context) => {
            const { label } = context[0];
            return `Citation Month ${label}`;
          },
          title: (context) => {
            const { formattedValue, dataset } = context[0];
            return `${dataset.label} Vehicles ${formattedValue}`;
          },
          afterTitle: (context) => {
            const { dataIndex } = context[0];
            return `Total: ${this.getTotalCount(dataIndex)}`;
          },
          label: function () {
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    maintainAspectRatio: false,
  };
  public chartLegend = window.innerHeight > DeviceMaxSize.TABLET_VERTICAL;

  ngAfterViewInit(): void {
    this.updateChartData();
  }

  generateChartData(): [number[], number[], number[], number[]] {
    const filteredNewData = new Array<number>(12).fill(0);
    const totalNewData = new Array<number>(12).fill(0);
    const filteredOldData = new Array<number>(12).fill(0);
    const totalOldData = new Array<number>(12).fill(0);

    for (const c of this.vehicles) {
      totalNewData[getNumberMonthByName(c.label)] = c.newTotalCount;
      totalOldData[getNumberMonthByName(c.label)] = c.oldTotalCount;
      filteredNewData[getNumberMonthByName(c.label)] = c.newCount;
      filteredOldData[getNumberMonthByName(c.label)] = c.oldCount;
    }

    return [filteredNewData, totalNewData, filteredOldData, totalOldData];
  }

  updateChartData(): void {
    const [filteredNewData, totalNewData, filteredOldData, totalOldData] = this.generateChartData();

    if (!this.chart || !this.chart.chart) return;

    this.chart.chart.data.datasets[0].data = totalNewData;
    this.chart.chart.data.datasets[1].data = totalOldData;

    this.filteredNewData = filteredNewData;
    this.totalNewData = totalNewData;
    this.filteredOldData = filteredOldData;
    this.totalOldData = totalOldData;

    this.chart.chart.update();
  }

  getBarBackgroundColor(
    context: ScriptableContext<'bar'>,
    color: string,
    totalCollection: number[],
    filteredCollection: number[],
  ) {
    if (context.dataIndex === undefined || context.dataIndex < 0) return color;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const element = (context as any).element; // there is no such in TS, but it really exists;
    if (!element?.y || !element?.height) return color;

    const ctx = context.chart.ctx;
    const gradient = ctx.createLinearGradient(0, element.y + element.height, 0, element.y);

    let percent = 1;
    if (
      this.selections[this.name].length > 0 &&
      !this.selections[this.name].includes(this.chartLabels[context.dataIndex].toUpperCase())
    ) {
      percent = 0;
    } else if (totalCollection[context.dataIndex] > 0) {
      percent = filteredCollection[context.dataIndex] / totalCollection[context.dataIndex];
    }

    gradient.addColorStop(0, color);
    gradient.addColorStop(percent, color);
    gradient.addColorStop(percent, color + '88');
    gradient.addColorStop(1, color + '88');
    gradient.addColorStop(1, color);

    return gradient;
  }

  // disable rule because this type came from library
  // eslint-disable-next-line @typescript-eslint/ban-types
  public onChartClick = (e: { event?: ChartEvent; active?: {}[] }) => {
    const element = e?.active?.[0] as ActiveElement | undefined;
    if (!e.event || !element) return;
    const newFilters = getNewChartFiltersByLabel(
      this.selections[this.name],
      e.event,
      this.chartLabels[element.index].toUpperCase(),
    );
    if (newFilters.anyChanges && this.setSelections) {
      this.setSelections.emit({
        newFilters: newFilters.newSelectionFilter,
        resetOther: newFilters.resetOtherSelectionFilters,
      });
    }
  };

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.updateLegend();
  }

  private updateLegend() {
    this.chartLegend = this.fullScreenMode || window.innerWidth >= DeviceMaxSize.TABLET_VERTICAL;

    if (!this.chartOptions.plugins?.legend) return;

    this.chartOptions.plugins.legend.labels = {
      ...this.chartOptions.plugins.legend.labels,
      padding: window.innerWidth <= DeviceMaxSize.TABLET_VERTICAL ? 16 : 8,
    };

    this.chartOptions.plugins.legend.position =
      this.fullScreenMode && window.innerWidth <= DeviceMaxSize.TABLET_VERTICAL ? 'bottom' : 'right';

    this.chartOptions.plugins.legend.title = {
      ...this.chartOptions.plugins.legend.title,
      position: this.fullScreenMode && window.innerWidth <= DeviceMaxSize.TABLET_VERTICAL ? 'start' : 'center',
    };
  }

  exportToImage(): Base64Image | null {
    return exportChartToBase64Image(this.chart);
  }
}
